import { useEffect, useRef, useState } from 'react';
import { aiApi, type AiConversation } from '../../api';
import Icon from '../common/Icon';
import { renderMarkdown } from '../../lib/markdown';

type PanelMessage = {
  role: 'user' | 'assistant';
  content: string;
  tools?: { name: string; input: Record<string, unknown> }[];
  pending?: boolean;
  error?: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  matterContext?: { id: number; reference: string; name: string } | null;
};

// localStorage key that survives page reload so the chat thread isn't lost
// every time the user refreshes. Cleared by resetToNew or `litigate:logout`.
const CONV_KEY = 'litigate.chat.conversation_id';

export default function ChatPanel({ open, onClose, matterContext }: Props) {
  const [conversationId, setConversationIdState] = useState<string | null>(() => localStorage.getItem(CONV_KEY));
  const [messages, setMessages] = useState<PanelMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [needsConfig, setNeedsConfig] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Wrap setConversationId so localStorage stays in sync.
  const setConversationId = (id: string | null) => {
    setConversationIdState(id);
    if (id) localStorage.setItem(CONV_KEY, id);
    else localStorage.removeItem(CONV_KEY);
  };

  // On first open with a stored conversationId but no messages in memory
  // (e.g. fresh page load), fetch the prior thread from the server so the
  // conversation feels continuous.
  useEffect(() => {
    if (!open || messages.length > 0 || !conversationId || historyLoading) return;
    let cancelled = false;
    setHistoryLoading(true);
    aiApi.conversation(conversationId)
      .then((rows) => {
        if (cancelled) return;
        const restored: PanelMessage[] = rows
          .filter((r) => r.role === 'user' || r.role === 'assistant')
          .map((r) => ({
            role: r.role === 'user' ? 'user' : 'assistant',
            content: r.content,
            tools: r.toolCalls?.map((tc) => ({ name: tc.name, input: tc.input })) || undefined,
          }));
        setMessages(restored);
      })
      .catch(() => {
        // Stale conversation id (deleted, wrong tenant, etc.) — start fresh.
        setConversationId(null);
      })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [open, conversationId, messages.length, historyLoading]);

  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Logout clears the chat thread along with auth state.
  useEffect(() => {
    const onLogout = () => {
      localStorage.removeItem(CONV_KEY);
      setConversationIdState(null);
      setMessages([]);
    };
    window.addEventListener('litigate:logout', onLogout);
    return () => window.removeEventListener('litigate:logout', onLogout);
  }, []);

  const resetToNew = () => {
    // Empty thread or already-fresh state — no confirmation needed.
    if (messages.length === 0 && !conversationId) {
      setNeedsConfig(false);
      return;
    }
    // Confirm before wiping a non-empty thread. The current thread is still
    // persisted server-side and recoverable from the conversation list (Phase
    // 4 sidebar) — but the user complained about losing context mid-flow when
    // the "+" button silently nuked the panel. Make destruction explicit.
    const ok = window.confirm(
      'Start a new thread?\n\nYour current conversation will be saved and remains accessible from the conversation history.',
    );
    if (!ok) return;
    setConversationId(null);
    setMessages([]);
    setNeedsConfig(false);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const composed = matterContext && messages.length === 0
      ? `[Context: matter ${matterContext.reference} — ${matterContext.name}]\n\n${text}`
      : text;

    setInput('');
    setMessages((m) => [
      ...m,
      { role: 'user', content: text },
      { role: 'assistant', content: '', pending: true },
    ]);
    setSending(true);
    try {
      const r = await aiApi.chat({ conversationId: conversationId || undefined, message: composed });
      if (!conversationId) setConversationId(r.conversationId);
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.pending) {
          copy[copy.length - 1] = { role: 'assistant', content: r.content, tools: r.toolsUsed };
        }
        return copy;
      });
    } catch (err) {
      const e = err as { code?: string; message?: string };
      if (e.code === 'ai_not_configured' || e.code === 'ai_disabled' || e.code === 'ai_key_invalid') {
        setNeedsConfig(true);
      }
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.pending) {
          copy[copy.length - 1] = {
            role: 'assistant',
            content: e.message || 'Something went wrong talking to the AI.',
            error: true,
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) return null;
  return (
    <div style={overlay}>
      <div style={panel}>
        <div style={header}>
          <div className="row" style={{ gap: 8, flex: 1 }}>
            <Icon name="sparkle" size={16} style={{ color: 'var(--color-accent)' }} />
            <b style={{ fontSize: 14 }}>Litigate Assistant</b>
            {matterContext && (
              <span className="pill accent" style={{ fontSize: 10 }}>
                {matterContext.reference}
              </span>
            )}
          </div>
          <button
            className="ghost"
            onClick={() => setShowHistory((s) => !s)}
            title="Conversation history"
            style={iconBtn}
          >
            <Icon name="check-list" size={14} />
          </button>
          <button className="ghost" onClick={resetToNew} title="New conversation" style={iconBtn}>
            <Icon name="plus" size={14} />
          </button>
          <button className="ghost" onClick={onClose} title="Close" style={iconBtn}>
            <Icon name="x" size={14} />
          </button>
        </div>

        {showHistory && (
          <ConversationHistory
            currentId={conversationId}
            onPick={(id) => {
              setConversationId(id);
              setMessages([]);
              setShowHistory(false);
            }}
            onClose={() => setShowHistory(false)}
          />
        )}

        <div style={body}>
          {historyLoading && (
            <div className="muted" style={{ padding: 16, fontSize: 13 }}>Restoring previous conversation…</div>
          )}

          {messages.length === 0 && !needsConfig && !historyLoading && (
            <div className="muted" style={{ padding: 16, fontSize: 13 }}>
              Ask about a matter, deadlines, or draft a letter. Responses pull live data from this chambers — they won't invent party names or case numbers.
              {matterContext && <div style={{ marginTop: 8 }}>Anchored to <b>{matterContext.reference}</b>.</div>}
            </div>
          )}

          {needsConfig && (
            <div className="card" style={{ margin: 12, borderColor: 'var(--color-accent)' }}>
              <b>AI isn't set up yet.</b>
              <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                An administrator needs to add a provider API key under Settings → AI before you can chat.
              </p>
            </div>
          )}

          {messages.map((m, i) => (
            <Bubble key={i} m={m} />
          ))}
          <div ref={bottomRef} />
        </div>

        <div style={footer}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={matterContext ? `Ask about ${matterContext.reference}…` : 'Ask anything about your practice…'}
            rows={2}
            style={{ resize: 'none', width: '100%' }}
          />
          <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
            <button className="primary" onClick={send} disabled={!input.trim() || sending}>
              {sending ? 'Thinking…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ m }: { m: PanelMessage }) {
  const isUser = m.role === 'user';
  // User messages stay literal (preserve their formatting). Assistant
  // messages get the markdown pass — bold/italic/code/lists/tables.
  // Errors (the assistant rendered an error message) also stay literal.
  const renderAsMarkdown = !isUser && !m.error && !m.pending;
  return (
    <div style={{ padding: '8px 14px', display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <div style={{
        maxWidth: '85%',
        background: isUser ? 'var(--color-accent)' : 'var(--color-surface-2)',
        color: isUser ? '#1A1305' : (m.error ? 'var(--color-danger)' : 'var(--color-text)'),
        padding: '10px 12px', borderRadius: 12,
        fontSize: 13, lineHeight: 1.45,
        whiteSpace: renderAsMarkdown ? 'normal' : 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {m.pending ? (
          <span className="muted">…</span>
        ) : renderAsMarkdown ? (
          renderMarkdown(m.content)
        ) : (
          m.content
        )}
        {m.tools && m.tools.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {m.tools.map((t, i) => (
              <span key={i} className="pill" style={{ fontSize: 10 }}>{t.name}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', right: 20, bottom: 20, zIndex: 100,
};

const panel: React.CSSProperties = {
  width: 380, height: 540, maxHeight: '85vh',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  boxShadow: 'var(--shadow)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};

const header: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
};

const body: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: '8px 0',
};

const footer: React.CSSProperties = {
  borderTop: '1px solid var(--color-border)',
  padding: 10,
  background: 'var(--color-surface-2)',
};

const iconBtn: React.CSSProperties = {
  width: 28, height: 28, padding: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// Conversation history dropdown — surfaced from the panel header. Lets the
// advocate switch threads without losing prior context. Backed by the existing
// /ai/conversations endpoint (already tenant-scoped server-side).
function ConversationHistory({
  currentId, onPick, onClose,
}: {
  currentId: string | null;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [items, setItems] = useState<AiConversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    aiApi.conversations()
      .then((rows) => setItems(rows || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{
      borderBottom: '1px solid var(--color-border)',
      maxHeight: 240, overflowY: 'auto',
      background: 'var(--color-surface-2)',
    }}>
      <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: 12 }}>Recent threads</b>
        <span className="muted" style={{ fontSize: 11 }}>(this chambers only)</span>
        <button className="ghost" onClick={onClose} style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11 }}>Close</button>
      </div>
      {loading && <div className="muted" style={{ padding: 12, fontSize: 12 }}>Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="muted" style={{ padding: 12, fontSize: 12 }}>No previous threads.</div>
      )}
      {!loading && items.map((c) => {
        const isCurrent = currentId === c.id;
        return (
          <button
            key={c.id}
            className="ghost"
            onClick={() => onPick(c.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 12px', borderRadius: 0,
              background: isCurrent ? 'rgba(201,163,74,0.12)' : 'transparent',
              fontSize: 12,
            }}
          >
            <div style={{ fontWeight: isCurrent ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {c.preview || '(no preview)'}
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              {c.lastMessageAt}{isCurrent ? ' · current' : ''}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Floating launch button that toggles the panel open/closed.
export function ChatLauncher({ onClick, active }: { onClick: () => void; active: boolean }) {
  return (
    <button
      onClick={onClick}
      title="Litigate Assistant"
      style={{
        position: 'fixed', right: 20, bottom: 20, zIndex: 99,
        width: 52, height: 52, borderRadius: '50%', padding: 0,
        display: active ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--color-accent)', color: '#1A1305',
        border: 'none', boxShadow: 'var(--shadow)', cursor: 'pointer',
      }}
    >
      <Icon name="sparkle" size={22} />
    </button>
  );
}
